import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { Post } from '@/services/posts';
import { Calendar } from '@/types/calendar';
import { CalendarNote } from '@/types/calendar-notes';
import { CalendarHoliday } from '@/types/holidays';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { RetryImage, PlaceholderImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useTimezone } from '@/contexts';
import { getContrastColor } from '@/utils/colors';
import { HolidayEditPopover } from './HolidayEditPopover';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { getInferredStatus, getStatusInfo, getStatusBadge, isOccurrenceStatus } from '@/utils/calendarStatus';
import { CalendarPostPopover } from './PostPopover';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { getStackZIndex } from '@/contexts/ModalStackContext';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get post type label based on media content
 */
function getPostTypeLabel(post: Post): string {
  const media = post.postMedia;
  if (!media || media.length === 0) return 'Text';

  const hasVideo = media.some(m => m.type === 'VIDEO');
  const hasPdf = media.some(m => m.type === 'PDF');
  const imageCount = media.filter(m => m.type === 'IMAGE').length;

  if (hasVideo) return 'Video';
  if (hasPdf) return 'PDF';
  if (imageCount > 1) return 'Carousel';
  if (imageCount === 1) return 'Image';
  return 'Media';
}

/**
 * Get post type icon (HappyIcons)
 */
function getPostTypeIcon(post: Post): string {
  const media = post.postMedia;
  if (!media || media.length === 0) return ICONS.FILE; // Document icon for text-only posts

  const hasVideo = media.some(m => m.type === 'VIDEO');
  const hasPdf = media.some(m => m.type === 'PDF');
  const imageCount = media.filter(m => m.type === 'IMAGE').length;

  if (hasVideo) return ICONS.VIDEO; // 'slider-video'
  if (hasPdf) return ICONS.FILE; // 'file'
  if (imageCount > 1) return ICONS.GRID; // 'grid' for carousel/gallery
  if (imageCount === 1) return ICONS.IMAGE; // 'image'
  return ICONS.FILE;
}

// ============================================================================
// Card Components
// ============================================================================

interface PostCardProps {
  post: Post;
  onEdit: (postId: number, calendarMappingId?: number) => void;
  onDelete: (post: Post) => void;
  isDragging?: boolean;
  calendarColor?: string;
  /** Date for drag-and-drop sourceDate */
  date?: Date;
}

// ============================================================================
// Horizontal Post Card Component (Google Calendar style, for CalendarDayPosts)
// ============================================================================

const HorizontalPostCard = ({
  post,
  onEdit,
  onDelete,
  isDragging: globalIsDragging,
  calendarColor,
  date,
}: PostCardProps) => {
  const { t } = useTranslation('common');
  const { formatTimeOnly } = useTimezone();
  const navigate = useNavigate();
  const { canEditPost } = usePostPermissions();

  const isBlog = post.source === 'blog';
  const inferredStatus = getInferredStatus(post);
  // MAPPED and SCHEDULED can be dragged (drag reschedules QUEUED instances)
  // Blog entries are READ-ONLY: never draggable/editable/deletable
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');
  // Mapped items still open for review/scheduling; queued content opens for Edit
  // only when its release verdict permits content changes.
  const canEdit = !isBlog && (inferredStatus === 'MAPPED' || (inferredStatus === 'SCHEDULED' && canEditPost(post)));
  // MAPPED and SCHEDULED can be removed; SCHEDULED cancels instances first
  const canDelete = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // Use CalendarMapping (NEW system) - calendarPost is deprecated
  // mappingId is set per-entry from calendar endpoint, ensuring uniqueness for multi-mapped posts
  const mappingId = post.mappingId;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // Use mappingId in ID to ensure uniqueness when same post has multiple mappings.
    // Blog entries use a namespaced id so they never collide with a real social post's id.
    id: isBlog ? `blog-post-${post.blogPostId}` : `calendar-post-${mappingId || post.id}`,
    data: {
      type: 'calendar-post',
      post,
      mappingId, // Include specific mappingId for move operations
      sourceDate: date?.toISOString() || new Date().toISOString(),
    },
    disabled: !canDrag || isBlog,
  });

  const handleClick = () => {
    if (isBlog) {
      navigate(`/blog/${post.blogPostId}`);
      return;
    }
    if (canEdit) onEdit(post.id, mappingId);
  };

  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);
  // Stage 2c-ii: `post.status` on a calendar occurrence entry IS the
  // first-class `OccurrenceStatus` (see docs/architecture/POST-STATUS-MODEL.md
  // + `calendarService.getCalendarPosts`/`resolveOccurrenceStatus`) — layer
  // its label/color/icon (`getStatusBadge`) over the base gradient/
  // headerTextColor from `getStatusInfo` so Partial/Missed/Cancelled/
  // Publishing render as themselves. Falls back to the legacy translated
  // label entirely when `post.status` isn't a genuine occurrence value
  // (defensive).
  const statusInfo = isOccurrenceStatus(post.status)
    ? { ...getStatusInfo(inferredStatus, t), ...getStatusBadge(post.status, t) }
    : getStatusInfo(inferredStatus, t);

  return (
    <HoverCard
      trigger={
        <div
          ref={setNodeRef}
          {...(canDrag ? { ...listeners, ...attributes } : {})}
          onClick={handleClick}
          className={cn(
            'relative h-20 rounded-xl overflow-hidden',
            'cursor-pointer transition-shadow duration-200 ease-out',
            'shadow-sm group',
            canDrag && 'cursor-grab active:cursor-grabbing',
            isDragging && 'opacity-50',
            !isDragging && 'hover:shadow-lg',
            globalIsDragging && !isDragging && 'opacity-70'
          )}
          role="button"
          tabIndex={0}
          aria-label={`Post: ${post.caption?.substring(0, 50) || 'No caption'}`}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
        >
      {/* Calendar color indicator - left border */}
      {calendarColor && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 z-10"
          style={{ backgroundColor: calendarColor }}
        />
      )}

      {/* Background - Image or Gradient */}
      {hasMedia ? (
        <RetryImage
          src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-r ${statusInfo.gradient} transition-transform duration-300 group-hover:scale-105`} />
      )}

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />

      {/* Content */}
      <div className="absolute inset-0 flex items-center px-4 gap-4">
        {/* Left: Time and Status */}
        <div className="flex-shrink-0 text-white">
          {post.scheduledAt ? (
            <div className="text-lg font-bold">{formatTimeOnly(post.scheduledAt)}</div>
          ) : (
            <div className="text-sm font-medium text-white/70">{t('no_time')}</div>
          )}
          <div className="flex items-center gap-1 mt-0.5">
            <Icon name={statusInfo.icon} size="xs" className="text-white/80" ariaHidden />
            <span className="text-xs text-white/70">{statusInfo.label}</span>
          </div>
        </div>

        {/* Center: Caption */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium line-clamp-2 whitespace-pre-wrap">
            {post.caption || t('no_caption')}
          </p>
        </div>

        {/* Right: Badges and Actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Blog badge — distinguishes read-only WordPress blog chips from social posts */}
          {isBlog ? (
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
              <Icon name={ICONS.BLOG} size="xs" className="text-white" ariaHidden />
              <span className="text-xs text-white font-medium">{t('blog_page_title', { defaultValue: 'Blog' })}</span>
            </div>
          ) : (
          /* Post type badge */
          <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
            <Icon name={getPostTypeIcon(post)} size="xs" className="text-white" ariaHidden />
            <span className="text-xs text-white font-medium">{getPostTypeLabel(post)}</span>
          </div>
          )}

          {/* Platform count badge - only show for scheduled/published posts, not MAPPED */}
          {!isBlog && post.postChannels && post.postChannels.length > 0 && inferredStatus !== 'MAPPED' && (
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
              <Icon name={ICONS.SHARE} size="xs" className="text-white" ariaHidden />
              <span className="text-xs text-white font-medium">{post.postChannels.length}</span>
            </div>
          )}

          {/* Hover actions - only for non-committed posts */}
          {(canEdit || canDelete) && (
          <div className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity flex gap-1">
            {canEdit && (
            <Tooltip content={t('edit')}>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(post.id, mappingId); }}
                className="w-8 h-8 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white transition-colors"
                aria-label={t('edit')}
              >
                <Icon name={ICONS.EDIT} size="sm" className="text-gray-600" ariaHidden />
              </button>
            </Tooltip>
            )}
            {canDelete && (
            <Tooltip content={t('remove_from_calendar')}>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(post); }}
                className="w-8 h-8 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-red-50 transition-colors"
                aria-label={t('remove_from_calendar')}
              >
                <Icon name={ICONS.TRASH} size="sm" className="text-red-500" ariaHidden />
              </button>
            </Tooltip>
            )}
          </div>
          )}
        </div>
      </div>
        </div>
      }
      side="left"
      align="start"
      sideOffset={8}
      openDelay={100}
      closeDelay={100}
      open={isDragging ? false : undefined}
    >
      <CalendarPostPopover post={post} inferredStatus={inferredStatus} onEdit={() => onEdit(post.id, mappingId)} onDelete={() => onDelete(post)} />
    </HoverCard>
  );
};

// ============================================================================
// Square Post Card Component (for grid layout in CalendarDayView)
// ============================================================================

interface SquarePostCardProps {
  post: Post;
  onEdit: (postId: number, calendarMappingId?: number) => void;
  onDelete: (post: Post) => void;
  isDragging?: boolean;
  calendarColor?: string;
  /** Date for drag-and-drop sourceDate */
  date?: Date;
}

export const SquarePostCard = ({
  post,
  onEdit,
  onDelete,
  isDragging: globalIsDragging,
  calendarColor,
  date,
}: SquarePostCardProps) => {
  const { t } = useTranslation('common');
  const { formatTimeOnly } = useTimezone();
  const navigate = useNavigate();
  const { canEditPost } = usePostPermissions();
  const [touchActionsOpen, setTouchActionsOpen] = useState(false);

  const isBlog = post.source === 'blog';
  const inferredStatus = getInferredStatus(post);
  // MAPPED and SCHEDULED can be dragged (drag reschedules QUEUED instances)
  // Blog entries are READ-ONLY: never draggable/editable/deletable
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');
  // Mapped items still open for review/scheduling; queued content opens for Edit
  // only when its release verdict permits content changes.
  const canEdit = !isBlog && (inferredStatus === 'MAPPED' || (inferredStatus === 'SCHEDULED' && canEditPost(post)));
  // MAPPED and SCHEDULED can be removed; SCHEDULED cancels instances first
  const canDelete = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // Extract calendar mapping ID for shooting mode
  // Use CalendarMapping (NEW system) - calendarPost is deprecated
  // mappingId is set per-entry from calendar endpoint, ensuring uniqueness for multi-mapped posts
  const mappingId = post.mappingId;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // Use mappingId in ID to ensure uniqueness when same post has multiple mappings.
    // Blog entries use a namespaced id so they never collide with a real social post's id.
    id: isBlog ? `blog-post-${post.blogPostId}` : `calendar-post-${mappingId || post.id}`,
    data: {
      type: 'calendar-post',
      post,
      mappingId, // Include specific mappingId for move operations
      sourceDate: date?.toISOString() || new Date().toISOString(),
    },
    disabled: !canDrag || isBlog,
  });

  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);
  // Stage 2c-ii: `post.status` on a calendar occurrence entry IS the
  // first-class `OccurrenceStatus` (see docs/architecture/POST-STATUS-MODEL.md
  // + `calendarService.getCalendarPosts`/`resolveOccurrenceStatus`) — layer
  // its label/color/icon (`getStatusBadge`) over the base gradient/
  // headerTextColor from `getStatusInfo` so Partial/Missed/Cancelled/
  // Publishing render as themselves. Falls back to the legacy translated
  // label entirely when `post.status` isn't a genuine occurrence value
  // (defensive).
  const statusInfo = isOccurrenceStatus(post.status)
    ? { ...getStatusInfo(inferredStatus, t), ...getStatusBadge(post.status, t) }
    : getStatusInfo(inferredStatus, t);

  return (
    <>
    <HoverCard
      trigger={
        <div
          ref={setNodeRef}
          {...(canDrag ? { ...listeners, ...attributes } : {})}
          onClick={() => {
            if (isBlog) {
              navigate(`/blog/${post.blogPostId}`);
              return;
            }
            canEdit && onEdit(post.id, mappingId);
          }}
          className={cn(
            'relative aspect-square rounded-xl overflow-hidden',
            'cursor-pointer transition-shadow duration-300 ease-out',
            'bg-gray-100 shadow-sm group',
            canDrag && 'cursor-grab active:cursor-grabbing',
            isDragging && 'opacity-50',
            !isDragging && 'hover:shadow-xl hover:z-10',
            globalIsDragging && !isDragging && 'opacity-70'
          )}
          role="button"
          tabIndex={0}
          aria-label={`Post: ${post.caption?.substring(0, 50) || 'No caption'}`}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isBlog) {
                navigate(`/blog/${post.blogPostId}`);
                return;
              }
              if (canEdit) onEdit(post.id, mappingId);
            }
          }}
        >
        {/* Calendar color indicator */}
        {calendarColor && (
          <div className="absolute left-0 top-0 bottom-0 w-1 z-10" style={{ backgroundColor: calendarColor }} />
        )}

        {/* Thumbnail */}
        {hasMedia ? (
          <RetryImage
            src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
            alt=""
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <PlaceholderImage />
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Status icon (top-right) */}
        <Tooltip content={statusInfo.label}>
          <div className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
            <Icon name={statusInfo.icon} size="sm" className={statusInfo.color} ariaHidden />
          </div>
        </Tooltip>

        {/* Time badge (bottom-left) */}
        {post.scheduledAt && (
          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-1">
            <Icon name={ICONS.CLOCK} size="xs" ariaHidden />
            {formatTimeOnly(post.scheduledAt)}
          </div>
        )}

        {/* Blog badge (top-left) — distinguishes read-only WordPress blog chips from social posts */}
        {isBlog && (
          <Tooltip content={t('blog_page_title', { defaultValue: 'Blog' })}>
            <div className="absolute top-2 left-2 w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
              <Icon name={ICONS.BLOG} size="sm" className="text-gray-600" ariaHidden />
            </div>
          </Tooltip>
        )}

        {/* Platform count removed per user feedback */}

        {/* Compact touch cards use the card sheet. Touch tablets get one
            full-size action trigger; pointer desktops keep hover actions. */}
        {(canEdit || canDelete) && (
        <>
        <div className="absolute top-2 left-2 z-20 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:hidden">
          {canEdit && (
            <Tooltip content={t('edit')}>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(post.id, mappingId); }}
                className="w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white transition-colors"
                aria-label={t('edit')}
              >
                <Icon name={ICONS.EDIT} size="xs" className="text-gray-600" ariaHidden />
              </button>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip content={t('remove_from_calendar')}>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(post); }}
                className="w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm hover:bg-orange-50 transition-colors"
                aria-label={t('remove_from_calendar')}
              >
                <Icon name={ICONS.CALENDAR} size="xs" className="text-orange-500" ariaHidden />
              </button>
            </Tooltip>
          )}
        </div>
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); setTouchActionsOpen(true); }}
          onKeyDown={(e) => e.stopPropagation()}
          className="absolute left-1 top-1 z-20 hidden h-[44px] w-[44px] items-center justify-center rounded-lg border border-transparent focus:outline-none focus-visible:border-gray-700 lg:[@media(hover:none)]:flex"
          aria-label={t('actions')}
          aria-haspopup="dialog"
          aria-expanded={touchActionsOpen}
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/90 text-gray-700 shadow-sm backdrop-blur-sm">
            <Icon name={ICONS.ELLIPSIS_VERTICAL} size="xs" ariaHidden />
          </span>
        </button>
        </>
        )}

        {/* Media count badge */}
        {post.postMedia && post.postMedia.length > 1 && (
          <div className={cn(
            'absolute top-2 left-2 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded',
            (canEdit || canDelete) && 'lg:[@media(hover:none)]:top-auto lg:[@media(hover:none)]:left-auto lg:[@media(hover:none)]:bottom-2 lg:[@media(hover:none)]:right-2'
          )}>
            +{post.postMedia.length - 1}
          </div>
        )}
        </div>
      }
      side="left"
      align="start"
      sideOffset={8}
      openDelay={100}
      closeDelay={100}
      open={isDragging ? false : undefined}
    >
      <CalendarPostPopover post={post} inferredStatus={inferredStatus} onEdit={() => onEdit(post.id, mappingId)} onDelete={() => onDelete(post)} />
    </HoverCard>
    {(canEdit || canDelete) && (
      <BottomSheet open={touchActionsOpen} onOpenChange={setTouchActionsOpen} className="mx-auto max-w-md" showDragHandle={false}>
        <div className="px-4 pb-4 pt-5">
          <p className="mb-3 line-clamp-2 pr-12 text-sm font-medium text-gray-900">{post.caption || t('no_caption')}</p>
          <div className="space-y-1">
            {canEdit && (
              <button
                type="button"
                onClick={() => { setTouchActionsOpen(false); onEdit(post.id, mappingId); }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-left text-sm text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-gray-50"
              >
                <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                {t('edit')}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => { setTouchActionsOpen(false); onDelete(post); }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-left text-sm text-red-700 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:bg-red-50"
                aria-label={t('remove_from_calendar')}
              >
                <Icon name={ICONS.CALENDAR} size="sm" ariaHidden />
                {t('remove')}
              </button>
            )}
          </div>
        </div>
      </BottomSheet>
    )}
    </>
  );
};

/**
 * CalendarDayPosts Component
 *
 * Slide-in panel showing posts for a selected day.
 * Features:
 * - List of posts with preview
 * - Platform badges
 * - Status indicators
 * - Actions: Edit, Delete, Reschedule
 * - "Create Post" button for that day
 */

interface CalendarDayPostsProps {
  /**
   * Selected date
   */
  date: Date;

  /**
   * Posts for this date
   */
  posts: Post[];

  /**
   * Whether the panel is open
   */
  isOpen: boolean;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Create post handler (with pre-filled date)
   */
  onCreatePost: (date: Date) => void;

  /**
   * Edit post handler
   */
  onEditPost: (postId: number, calendarMappingId?: number) => void;

  /**
   * Request removal of this exact calendar entry
   */
  onDeletePost: (post: Post) => void;

  /**
   * Reschedule post handler
   */
  onReschedulePost?: (postId: number) => void;

  /**
   * Map post handler (allowed for any date)
   */
  onMapPost?: (date: Date) => void;

  /**
   * Create campaign handler
   */
  onCreateCampaign?: (date: Date) => void;

  /**
   * Click on campaign to edit
   */
  onCampaignClick?: (campaign: CalendarNote) => void;

  /**
   * Delete campaign handler
   */
  onDeleteCampaign?: (campaignId: number) => void;

  /**
   * Whether drag-and-drop is globally active
   */
  isDraggingActive?: boolean;

  /**
   * Available calendars (for showing calendar colors)
   */
  calendars?: Calendar[];

  /**
   * Campaigns for this day
   */
  campaigns?: CalendarNote[];

  /**
   * Holidays for this day
   */
  holidays?: CalendarHoliday[];

  /**
   * Open holidays management modal
   */
  onManageHolidays?: () => void;
}

export const CalendarDayPosts = ({
  date,
  posts,
  isOpen,
  onClose,
  onEditPost,
  onDeletePost,
  onCampaignClick,
  onDeleteCampaign,
  // Note: onReschedulePost is available for future use with click-to-reschedule dialogs
  // Currently drag-and-drop handles rescheduling in CalendarGrid
  isDraggingActive = false,
  calendars = [],
  campaigns = [],
  holidays = [],
  onManageHolidays,
}: CalendarDayPostsProps) => {
  const { t } = useTranslation('common');
  const { canEditCalendar } = useReviewerRestrictions();
  const [deleteCampaignId, setDeleteCampaignId] = useState<number | null>(null);
  const { formatWithWeekday } = useTimezone();

  const formattedDate = formatWithWeekday(date);
  const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  // Lock body scroll when drawer is open (prevents double scrollbar on mobile)
  useBodyScrollLock(isOpen);

  // Proper layer in the modal stack: Esc closes only when topmost, z-index comes from the stack
  const stackZ = getStackZIndex(useEscapeLayer(isOpen, onClose));

  /**
   * Get calendar color for a post
   * Uses CalendarMapping (NEW system) - calendarPost is deprecated
   */
  const getCalendarColor = (post: Post): string | undefined => {
    // Use CalendarMapping's calendarId (NEW system)
    const calendarId = post.calendarMappings?.find((entry) => entry.id === post.mappingId)?.calendarId
      ?? post.calendarMappings?.[0]?.calendarId;
    if (!calendarId || calendars.length === 0) return undefined;

    const calendar = calendars.find((c) => c.id === calendarId);
    return calendar?.color;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 lg:hidden"
        style={{ zIndex: stackZ.backdrop }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 w-full sm:w-96 lg:w-[420px]',
          'bg-white shadow-2xl',
          'flex flex-col',
          'transform transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ zIndex: stackZ.content }}
        role="dialog"
        aria-label={formattedDate}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{formattedDate}</h2>
            <div className="flex flex-wrap gap-2 text-sm text-gray-500">
              <span>{t('x_posts', { count: posts.length })}</span>
              {campaigns.length > 0 && (
                <span className="flex items-center gap-1">
                  <Icon name={ICONS.FLAG} size="xs" ariaHidden />
                  {t('x_campaigns', { count: campaigns.length })}
                </span>
              )}
              {holidays.length > 0 && (
                <span className="flex items-center gap-1">
                  <Icon name={ICONS.STAR} size="xs" ariaHidden />
                  {t('x_holidays', { count: holidays.length })}
                </span>
              )}
              {isToday && <span>{t('today_suffix')}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg transition-colors"
            aria-label={t('close_panel')}
          >
            <Icon name={ICONS.CLOSE} size="sm" color="text-gray-500" ariaHidden />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Campaigns and Holidays */}
          {(campaigns.length > 0 || holidays.length > 0) && (
            <div className="space-y-2 mb-4">
              {/* Campaign badges */}
              {campaigns.map((campaign) => {
                const bgColor = campaign.color || '#3B82F6';
                const textColor = getContrastColor(bgColor);
                return (
                  <div
                    key={campaign.id}
                    className="rounded-lg overflow-hidden"
                    style={{ backgroundColor: bgColor }}
                  >
                    <div className="flex items-start justify-between px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm" style={{ color: textColor }}>
                            {campaign.title}
                          </span>
                          {campaign.repeatType && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded opacity-80"
                              style={{
                                backgroundColor: textColor === '#000000' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
                                color: textColor
                              }}
                            >
                              {t(`repeat_${campaign.repeatType.toLowerCase()}`)}
                            </span>
                          )}
                        </div>
                        {campaign.description && (
                          <p className="text-xs mt-0.5 opacity-90" style={{ color: textColor }}>
                            {campaign.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Tooltip content={t('edit')}>
                          <button
                            onClick={() => onCampaignClick?.(campaign)}
                            className="p-1 hover:opacity-70 transition-opacity"
                            aria-label={t('edit')}
                          >
                            <Icon name={ICONS.EDIT} size="xs" style={{ color: textColor }} ariaHidden />
                          </button>
                        </Tooltip>
                        <Tooltip content={t('delete')}>
                          <button
                            onClick={() => setDeleteCampaignId(campaign.id)}
                            className="p-1 hover:opacity-70 transition-opacity"
                            aria-label={t('delete')}
                          >
                            <Icon name={ICONS.TRASH} size="xs" style={{ color: textColor }} ariaHidden />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Holiday badges */}
              {holidays.map((holiday) => {
                const badge = (
                  <div
                    className="px-3 py-2 rounded-lg font-medium text-sm cursor-pointer hover:opacity-90 transition-opacity"
                    style={{
                      backgroundColor: holiday.colorCode || '#FFD700',
                      color: getContrastColor(holiday.colorCode || '#FFD700'),
                    }}
                  >
                    {holiday.localName}
                    {holiday.countryName && (
                      <p className="text-xs mt-0.5 opacity-90">{holiday.countryName}</p>
                    )}
                  </div>
                );
                // Editing/deleting holidays changes the calendar: reviewer-only people just see them
                return canEditCalendar ? (
                  <HolidayEditPopover
                    key={holiday.id}
                    holiday={holiday}
                    onManageHolidays={() => onManageHolidays?.()}
                  >
                    {badge}
                  </HolidayEditPopover>
                ) : (
                  <div key={holiday.id}>{badge}</div>
                );
              })}
            </div>
          )}

          {posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Icon name={ICONS.CALENDAR} size="lg" color="text-gray-400" ariaHidden />
              </div>
              <p className="text-gray-600 mb-2">{t('no_posts_scheduled_for_day')}</p>
              <p className="text-sm text-gray-500">
                {t('use_plus_to_add_content', { defaultValue: 'Use the + button on the calendar to add content' })}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <HorizontalPostCard
                  key={post.source === 'blog' ? `blog-${post.blogPostId}` : `${post.id}-${post.mappingId || 'unmapped'}`}
                  post={post}
                  date={date}
                  onEdit={onEditPost}
                  onDelete={() => onDeletePost(post)}
                  isDragging={isDraggingActive}
                  calendarColor={getCalendarColor(post)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - Actions */}
      </div>

      {/* Delete Campaign Confirmation Dialog */}
      <ConfirmDialog
        open={deleteCampaignId !== null}
        onOpenChange={(open) => !open && setDeleteCampaignId(null)}
        title={t('delete_campaign')}
        description={t('delete_campaign_confirm')}
        variant="danger"
        confirmText={t('delete')}
        cancelText={t('cancel')}
        onConfirm={() => {
          if (deleteCampaignId) {
            onDeleteCampaign?.(deleteCampaignId);
            setDeleteCampaignId(null);
          }
        }}
        onCancel={() => setDeleteCampaignId(null)}
      />
    </>
  );
};
